import React, { useEffect, useState } from 'react';

import { faEnvelope, faUser } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Badge,
  Button,
  Group,
  LoadingOverlay,
  Modal,
  Pagination,
  Paper,
  Table,
  Text,
  Title
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';

import type { CareManager } from './types';
import { PostJSON, usePostJSON } from '../../utils/request';

interface CareManagerListResponse {
  success: boolean;
  careManagers: CareManager[];
  total: number;
  page: number;
  limit: number;
}

interface CareManagerListProps {
  title: string;
}

const CareManagerList: React.FC<CareManagerListProps> = ({ title }) => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [deleteTarget, setDeleteTarget] = useState<CareManager | null>(null);

  const {
    data,
    error,
    loading: isLoading,
    refetch
  } = usePostJSON<CareManagerListResponse>(
    '/api/care_manager/data',
    { page, limit },
    [page, limit]
  );

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const totalPages = Math.ceil((data?.total || 0) / limit);
  const careManagers = data?.careManagers || [];
  const total = data?.total || 0;

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await PostJSON<{ success: boolean }, { id: number }>(
        '/api/care_manager/delete',
        { id: deleteTarget.id }
      );
      notifications.show({
        title: 'Success',
        message: 'Care manager deleted successfully',
        color: 'green'
      });
      setDeleteTarget(null);
      void refetch();
    } catch (e) {
      notifications.show({
        title: 'Error',
        message: e instanceof Error ? e.message : 'Failed to delete care manager',
        color: 'red'
      });
    }
  };

  if (error) {
    return (
      <Paper withBorder p="md">
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Paper>
    );
  }

  return (
    <>
      <Paper withBorder p="md" style={{ position: 'relative' }}>
        <LoadingOverlay visible={isLoading} />

        <Group justify="space-between" mb="md">
          <Title order={3}>{title}</Title>
          <Badge color="blue" variant="light">
            {total} {total === 1 ? 'care manager' : 'care managers'}
          </Badge>
        </Group>

        {careManagers.length === 0 ? (
          <Text c="dimmed" py="xl" ta="center">
            No care managers found.
          </Text>
        ) : (
          <>
            <Table highlightOnHover striped layout="fixed">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w="30%">Name</Table.Th>
                  <Table.Th w="30%">Email</Table.Th>
                  <Table.Th w="15%">Phone</Table.Th>
                  <Table.Th w="25%">Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {careManagers.map((cm) => (
                  <Table.Tr key={cm.id}>
                    <Table.Td style={{ maxWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <FontAwesomeIcon icon={faUser} size="sm" style={{ flexShrink: 0 }} />
                        <Text truncate fw={500}>
                          {cm.firstName} {cm.lastName}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td style={{ maxWidth: 0 }}>
                      <Group gap="xs" wrap="nowrap">
                        <FontAwesomeIcon icon={faEnvelope} size="sm" style={{ flexShrink: 0 }} />
                        <Text truncate size="sm">
                          {cm.email}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">
                        {cm.phone || '\u2014'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" justify="flex-start">
                        <Button
                          component={Link}
                          size="xs"
                          to={`/care_manager/edit/${cm.id}`}
                          variant="light"
                        >
                          Edit
                        </Button>
                        <Button
                          color="red"
                          size="xs"
                          variant="light"
                          onClick={() => setDeleteTarget(cm)}
                        >
                          Delete
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center" mt="md">
                <Pagination
                  total={totalPages}
                  value={page}
                  onChange={setPage}
                />
              </Group>
            )}
          </>
        )}
      </Paper>

      <Modal
        centered
        opened={!!deleteTarget}
        title="Delete Care Manager"
        onClose={() => setDeleteTarget(null)}
      >
        <Text mb="lg">
          Are you sure you want to delete{' '}
          <strong>{deleteTarget?.firstName} {deleteTarget?.lastName}</strong>?
          This care manager must not have any assigned clients.
        </Text>
        <Group justify="flex-end">
          <Button variant="outline" onClick={() => setDeleteTarget(null)}>
            Cancel
          </Button>
          <Button color="red" onClick={() => void handleDelete()}>
            Delete
          </Button>
        </Group>
      </Modal>
    </>
  );
};

export default CareManagerList;
